import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchDashboardOverview, type DashboardOverview } from '../../../api/dashboardApi';
import { BillingContext } from '../../../context/BillingContext';
import styles from './OverviewPanel.module.css';
import DataFreshness from '../../../components/DataFreshness';

/**
 * Personalised intelligence summary (SRS #9).
 *
 * Everything here is scoped by the backend to the caller's plan and entitled
 * jurisdictions — the same rule search applies — so the numbers a Business
 * customer covering the UK and Germany sees are UK and German numbers, not
 * the world's. The generic KPI cards below it remain global coverage.
 */

const fmt = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString());
const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
/** Whole days from today to an ISO date, or null when there is no date. */
const daysUntil = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.ceil((ms - Date.now()) / 86_400_000);
};

const tierLabel = (tier: string | null) => (tier ? tier.replace(/_/g, ' ') : 'No plan');

/**
 * One headline number.
 *
 * A zero used to render as a loud "0" with a meta line that still described a
 * count — "0 / Coming into force / next 90 days". Three of the four tiles are
 * window counts, so on a quiet month the screen led with three zeros and read
 * as a product with no data in it. That was the substance of "the current
 * dashboards are not relevant" (SRS #23).
 *
 * A zero now reads as a word, in the muted weight, and says what it means
 * rather than repeating the window.
 */
const Stat: React.FC<{ value: number | null | undefined; label: string; meta: string; quiet: string; testId?: string }> =
  ({ value, label, meta, quiet, testId }) => {
    const none = value === 0;
    return (
      <div className={styles.stat} data-testid={testId}>
        <span className={`${styles.statValue} ${none ? styles.statNone : ''}`}>{none ? 'None' : fmt(value)}</span>
        <span className={styles.statLabel}>{label}</span>
        <span className={styles.statMeta}>{none ? quiet : meta}</span>
      </div>
    );
  };

interface Props {
  token: string | null;
}

const FEATURE_LABELS: Record<string, string> = {
  multi_jurisdiction: 'Multi-jurisdiction',
  advanced_ai: 'AI assessments & comparison',
  custom_alerts: 'Custom alerts',
  export_reporting: 'Report export',
  team_collaboration: 'Team collaboration',
  predictive_analytics: 'Predictive analytics',
  api_access: 'API access',
  sso: 'Single sign-on',
  dedicated_support: 'Dedicated support',
};

export const OverviewPanel: React.FC<Props> = ({ token }) => {
  // Optional: the panel also renders where no BillingProvider is mounted (tests, admin preview).
  const entitlements = useContext(BillingContext)?.entitlements;
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchDashboardOverview(token)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load your overview'); });
    return () => { cancelled = true; };
  }, [token]);

  if (error) return null; // the global cards still render; nothing personal to show
  if (!data) {
    return <div className={styles.panel} aria-busy="true"><span className={styles.eyebrow}>Your coverage</span><p className={styles.empty}>Loading your overview…</p></div>;
  }

  const { plan, scope, corpus, by_jurisdiction, recent, upcoming, activity } = data;
  const entitled = scope.entitled_jurisdictions;
  const maxCount = Math.max(1, ...by_jurisdiction.map((j) => j.total));
  const assessments = activity.compliance_assessments + activity.risk_assessments + activity.governance_sessions;
  const ownActivity = activity.chats + assessments;
  const quietMonth = corpus.new_in_window === 0 && corpus.active_in_window === 0 && corpus.upcoming_effective === 0;
  const trialDays = plan.trial_ends_at ? Math.max(0, Math.ceil((new Date(plan.trial_ends_at).getTime() - Date.now()) / 86400000)) : null;

  // Nothing in scope. Which of the three it is decides what we say, because
  // only one of them is the customer's to fix: they have not chosen their
  // jurisdictions yet (the common case — until 5 Oct there was no screen that
  // let them), an administrator has not authorised a government account, or
  // the plan genuinely covers none.
  if (entitled !== null && entitled.length === 0) {
    const reason = scope.reason ?? 'no_selection';
    return (
      <section className={styles.panel} aria-label="Your coverage">
        <div className={styles.top}>
          <div>
            <span className={styles.eyebrow}>Your coverage</span>
            <h2 className={styles.title}>
              {reason === 'no_selection' ? 'Choose the jurisdictions you follow' : 'Nothing in your scope yet'}
            </h2>
            <div className={styles.scope} data-testid="empty-scope" data-reason={reason}>
              {reason === 'no_selection' && (
                <>
                  Policyora tracks regulations in far more places than one team needs to watch. Tell us which
                  matter to you and this view — and search, alerts and the assistant — will cover those.{' '}
                  {scope.jurisdiction_limit
                    ? <>Your plan covers <strong>{scope.jurisdiction_limit}</strong>.</>
                    : <>Your plan covers as many as you like.</>}
                  {' '}<Link to="/onboarding">Choose your jurisdictions</Link>.
                </>
              )}
              {reason === 'government_unauthorised' && (
                <>A government account is granted its jurisdictions by Policyora rather than choosing them.
                  Ask your administrator to authorise them, and this view will fill in.</>
              )}
              {reason === 'plan_covers_none' && (
                <>Your plan does not currently include any jurisdictions — <Link to="/pricing">see plans</Link>.</>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // A selection that matches nothing in the corpus. Different from having no
  // selection: the customer has chosen, the choice just has no content behind
  // it. Rendering "0 regulations across 0 jurisdictions" and four empty tiles
  // reads as a broken product rather than a scope to adjust.
  if (corpus.total === 0 && entitled !== null && entitled.length > 0) {
    return (
      <section className={styles.panel} aria-label="Your coverage">
        <div className={styles.top}>
          <div>
            <span className={styles.eyebrow}>Your coverage</span>
            <h2 className={styles.title}>Nothing in your scope yet</h2>
            <div className={styles.scope} data-testid="empty-scope" data-reason="no_content">
              Your selected jurisdictions (<strong>{entitled.filter((e) => e.length > 3).join(', ') || entitled.join(', ')}</strong>)
              {' '}match no regulations in the corpus yet. Adjust them in <Link to="/onboarding">your profile</Link>,
              {' '}or <Link to="/search">search all jurisdictions</Link>.
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.panel} aria-label="Your coverage">
      <div className={styles.top}>
        <div>
          <span className={styles.eyebrow}>Your coverage</span>
          <h2 className={styles.title}>
            {fmt(corpus.total)} regulations across {fmt(corpus.jurisdictions)} {corpus.jurisdictions === 1 ? 'jurisdiction' : 'jurisdictions'}
          </h2>
          <div className={styles.scope}>
            {entitled === null
              ? <>Scope: <strong>all jurisdictions</strong> — choose your operating jurisdictions in <Link to="/profile">Profile</Link> to personalise this view.</>
              : entitled.length === 0
                ? <>Your plan does not currently include any jurisdictions.</>
                : <>Scope: <strong>{entitled.join(', ')}</strong>{scope.jurisdiction_limit ? ` (plan covers up to ${scope.jurisdiction_limit})` : ''}</>}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <span className={`${styles.planChip} ${plan.status === 'trialing' ? styles.warn : ''}`}>
            {tierLabel(plan.tier)}{plan.status === 'trialing' && trialDays !== null ? ` · trial, ${trialDays}d left` : plan.status ? ` · ${plan.status}` : ''}
          </span>
          <DataFreshness />  {/* SRS #15: real refresh time, not the cache time */}
        </div>
      </div>

      <div className={styles.stats}>
        <Stat testId="stat-new" value={corpus.new_in_window} label="New in your scope"
              meta={`added in the last ${corpus.window_days} days`}
              quiet={`nothing added in the last ${corpus.window_days} days`} />
        <Stat testId="stat-activity" value={corpus.active_in_window} label="Legislative activity"
              meta={`actions in the last ${corpus.window_days} days`}
              quiet={`no movement in the last ${corpus.window_days} days`} />
        <Stat testId="stat-upcoming" value={corpus.upcoming_effective} label="Coming into force"
              meta={`next ${corpus.upcoming_window_days} days`}
              quiet={`nothing due in the next ${corpus.upcoming_window_days} days`} />
        <Stat testId="stat-yours" value={ownActivity} label="Your activity"
              meta={`${activity.chats} chats · ${assessments} assessments, ${activity.window_days}d`}
              quiet="you have not used the assistant or an assessment yet" />
      </div>

      {quietMonth && (
        /* All three corpus windows are empty. Rather than leave the reader to
           infer that from three "None"s, say it once and offer the two things
           that are actually worth doing on a quiet month. */
        <p className={styles.quietNote} data-testid="quiet-month">
          Nothing has moved in your scope in the last {corpus.window_days} days. Your scope still holds{' '}
          <strong>{fmt(corpus.total)}</strong> regulations — <Link to="/search">search them</Link>{' '}
          or <Link to="/alerts">set an alert</Link> to be told when something changes.
        </p>
      )}

      <div className={styles.columns}>
        {/* Three cards, built the same way: a heading, a line saying what the
            column measures, then the rows. They were three bare lists before,
            and a long instrument title ran past the right edge of the panel
            because the anchor inside the row was never constrained. */}
        <section className={styles.col} aria-label="By jurisdiction">
          <h3 className={styles.colTitle}>By jurisdiction</h3>
          <p className={styles.colNote} data-testid="bar-legend">
            Bar length is each jurisdiction&rsquo;s share of the largest one in your scope
            ({fmt(maxCount)} regulations).
          </p>
          {by_jurisdiction.length === 0 ? <p className={styles.empty}>Nothing in scope yet.</p> : (
            <ul className={styles.list}>
              {by_jurisdiction.map((j) => (
                <li key={`${j.name}-${j.code}`} className={styles.jurRow}>
                  <div className={styles.jurTop}>
                    <span className={styles.rowMain} title={j.name}>{j.name}</span>
                    <span className={styles.jurCount}>
                      {fmt(j.total)}
                      {j.new_in_window ? <span className={styles.newPill}>+{j.new_in_window}</span> : null}
                    </span>
                  </div>
                  <div className={styles.barTrack}>
                    <div className={styles.bar} style={{ width: `${Math.max(2, (j.total / maxCount) * 100)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.col} aria-label="Recently added">
          <h3 className={styles.colTitle}>Recently added</h3>
          <p className={styles.colNote}>What entered your scope most recently.</p>
          {recent.length === 0 ? <p className={styles.empty}>Nothing in scope yet.</p> : (
            <ul className={styles.list}>
              {recent.map((r) => (
                <li key={r.id} className={styles.itemRow}>
                  <Link to={`/regulations/${r.id}`} className={styles.itemTitle} title={r.title}>{r.title}</Link>
                  <span className={styles.itemMeta}>
                    <span className={styles.jurTag}>{r.jurisdiction_code || r.jurisdiction_name}</span>
                    <span>{day(r.added_at)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.col} aria-label="Coming into force">
          <h3 className={styles.colTitle}>Coming into force</h3>
          <p className={styles.colNote}>Effective within the next {corpus.upcoming_window_days} days.</p>
          {upcoming.length === 0 ? <p className={styles.empty}>No effective dates in the next {corpus.upcoming_window_days} days within your scope.</p> : (
            <ul className={styles.list}>
              {upcoming.map((r) => {
                const days = daysUntil(r.effective_date);
                return (
                  <li key={r.id} className={styles.itemRow}>
                    <Link to={`/regulations/${r.id}`} className={styles.itemTitle} title={r.title}>{r.title}</Link>
                    <span className={styles.itemMeta}>
                      <span className={styles.jurTag}>{r.jurisdiction_code || r.jurisdiction_name}</span>
                      {/* The date matters less than how long is left, which is
                          the thing a compliance team is actually counting. */}
                      <span className={days !== null && days <= 30 ? styles.dueSoon : undefined}>
                        {days === null ? day(r.effective_date)
                          : days <= 0 ? 'today'
                          : days === 1 ? 'tomorrow'
                          : `in ${days} days`}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {entitlements && (
        <div className={styles.plan}>
          <span className={styles.colTitle} style={{ margin: 0 }}>Your plan includes</span>
          {Object.entries(FEATURE_LABELS).map(([key, label]) => {
            const f = entitlements.features[key];
            if (!f) return null;
            return f.allowed
              ? <span key={key} className={styles.chip}>{label}</span>
              : <Link key={key} to="/pricing" state={{ changeOnly: true }} className={`${styles.chip} ${styles.chipLocked}`}
                      title={f.upgrade_to ? `Included in the ${f.upgrade_to.replace(/_/g, ' ')} plan` : 'Not in your plan'}>
                  {label} 🔒
                </Link>;
          })}
        </div>
      )}

      <div className={styles.foot}>
        <span>Latest addition in your scope: {day(corpus.corpus_updated_at)}</span>
        <span>
          {activity.latest_compliance ? <Link to={`/compliance/${activity.latest_compliance.session_id}`}>Latest compliance assessment ({activity.latest_compliance.status})</Link> : <Link to="/compliance">Start a compliance assessment</Link>}
          {' · '}
          {activity.latest_risk ? <Link to={`/risk/${activity.latest_risk.session_id}`}>Latest risk assessment ({activity.latest_risk.status})</Link> : <Link to="/risk">Start a risk assessment</Link>}
        </span>
      </div>
    </section>
  );
};

export default OverviewPanel;
